import React, { useState, useEffect } from 'react';
import {
  Box,
  Typography,
  Paper,
  Skeleton,
  Alert
} from '@mui/material';
import TrendingUpIcon from '@mui/icons-material/TrendingUp';
import TrendingDownIcon from '@mui/icons-material/TrendingDown';
import TrendingFlatIcon from '@mui/icons-material/TrendingFlat';
import CheckCircleIcon from '@mui/icons-material/CheckCircle';
import { getAccuracySummary } from '../../utils/analyticsApi';

const AccuracySummaryCard = ({ scope, organizationId, teamId }) => {
  const [summary, setSummary] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);

  useEffect(() => {
    fetchSummary();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [scope, organizationId, teamId]);

  const fetchSummary = async () => {
    setLoading(true);
    setError(null);
    try {
      const params = { scope };
      if (organizationId) params.organizationId = organizationId;
      if (teamId) params.teamId = teamId;
      const response = await getAccuracySummary(params);
      setSummary(response.data);
    } catch (err) {
      console.error('Error fetching accuracy summary:', err);
      setError('Failed to load accuracy summary');
    } finally {
      setLoading(false);
    }
  };

  const getTrendIcon = (trend) => {
    switch (trend) {
      case 'improving':
        return <TrendingUpIcon sx={{ fontSize: 32, color: '#10B981' }} />;
      case 'declining':
        return <TrendingDownIcon sx={{ fontSize: 32, color: '#EF4444' }} />;
      default:
        return <TrendingFlatIcon sx={{ fontSize: 32, color: '#6B7280' }} />;
    }
  };

  const getTrendLabel = (trend) => {
    switch (trend) {
      case 'improving':
        return 'Improving';
      case 'declining':
        return 'Declining';
      default:
        return 'Stable';
    }
  };

  const getTrendColor = (trend) => {
    switch (trend) {
      case 'improving':
        return '#10B981';
      case 'declining':
        return '#EF4444';
      default:
        return '#6B7280';
    }
  };

  const getAccuracyColor = (accuracy) => {
    if (accuracy >= 70) return '#10B981';
    if (accuracy >= 50) return '#F59E0B';
    return '#EF4444';
  };

  if (loading) {
    return (
      <Paper sx={{ p: 3 }}>
        <Skeleton variant="text" width={150} height={32} />
        <Skeleton variant="rectangular" height={80} sx={{ mt: 2 }} />
      </Paper>
    );
  }

  if (error) {
    return (
      <Alert severity="error" sx={{ mb: 2 }}>
        {error}
      </Alert>
    );
  }

  if (!summary || summary.totalItems === 0) {
    return (
      <Paper sx={{ p: 3, textAlign: 'center' }}>
        <CheckCircleIcon sx={{ fontSize: 48, color: '#9CA3AF', mb: 1 }} />
        <Typography variant="h6" color="text.secondary">
          No Accuracy Data Yet
        </Typography>
        <Typography variant="body2" color="text.disabled" sx={{ mt: 1 }}>
          Enter actual points in session retros to see accuracy metrics
        </Typography>
      </Paper>
    );
  }

  return (
    <Paper sx={{ p: 3 }}>
      <Typography variant="h6" gutterBottom>
        Overall Estimation Accuracy
      </Typography>

      <Box sx={{ display: 'flex', gap: 4, mt: 2, flexWrap: 'wrap' }}>
        {/* Main accuracy metric */}
        <Box sx={{ flex: 1, minWidth: 150 }}>
          <Typography variant="overline" color="text.secondary">
            Accuracy Rate
          </Typography>
          <Box sx={{ display: 'flex', alignItems: 'baseline', gap: 1 }}>
            <Typography
              variant="h2"
              sx={{
                fontWeight: 700,
                color: getAccuracyColor(summary.overallAccuracy)
              }}
            >
              {summary.overallAccuracy}%
            </Typography>
          </Box>
          <Typography variant="body2" color="text.secondary">
            {summary.totalItems} items with actuals
          </Typography>
        </Box>

        {/* Trend indicator */}
        <Box sx={{ flex: 1, minWidth: 150 }}>
          <Typography variant="overline" color="text.secondary">
            Trend
          </Typography>
          <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, mt: 0.5 }}>
            {getTrendIcon(summary.trend)}
            <Typography
              variant="h5"
              sx={{
                fontWeight: 600,
                color: getTrendColor(summary.trend)
              }}
            >
              {getTrendLabel(summary.trend)}
            </Typography>
          </Box>
          <Typography variant="body2" color="text.secondary">
            Recent: {summary.recentAccuracy}% ({summary.recentItemCount} items)
          </Typography>
        </Box>

        {/* Comparison */}
        {summary.olderItemCount > 0 && (
          <Box sx={{ flex: 1, minWidth: 150 }}>
            <Typography variant="overline" color="text.secondary">
              Change
            </Typography>
            <Box sx={{ display: 'flex', alignItems: 'baseline', gap: 1, mt: 0.5 }}>
              <Typography
                variant="h5"
                sx={{
                  fontWeight: 600,
                  color: summary.recentAccuracy >= summary.olderAccuracy ? '#10B981' : '#EF4444'
                }}
              >
                {summary.recentAccuracy >= summary.olderAccuracy ? '+' : ''}
                {summary.recentAccuracy - summary.olderAccuracy}%
              </Typography>
            </Box>
            <Typography variant="body2" color="text.secondary">
              vs. older: {summary.olderAccuracy}% ({summary.olderItemCount} items)
            </Typography>
          </Box>
        )}
      </Box>
    </Paper>
  );
};

export default AccuracySummaryCard;
